const ACCENT = "text-[#C2410C]";

export default function LayerPeekPrivacy() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className={`text-4xl font-extrabold tracking-tight ${ACCENT}`}>
            Privacy Policy - LayerPeek 3D Print File Viewer
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Effective date:{" "}
            <time dateTime="2026-09-15">September 15, 2026</time>
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <p>
            <strong>Cosmovex</strong> built <strong>LayerPeek</strong> as a
            free, ad-supported Android app for viewing 3D-printing files and
            making printable parts from templates. This Privacy Policy explains
            what information LayerPeek processes, what stays on your device,
            what is sent to third-party services, and the choices you have when
            using the app.
          </p>

          <h2 className={`text-2xl font-bold ${ACCENT}`}>In Short</h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              Your 3D files are opened, parsed, and rendered on your device.
              They are never uploaded to our servers or anyone else&apos;s.
            </li>
            <li>
              Parts you create with <strong>Make</strong> are generated on your
              device and saved only where you choose. The files are never
              uploaded.
            </li>
            <li>LayerPeek has no account and no sign-in.</li>
            <li>
              <strong>COSMO AI design chat is optional.</strong> Only if you use
              it, the message you type and the settings of the template you are
              working on are sent to our COSMO AI service and to Google&apos;s
              Gemini API to produce template settings. Your 3D files are never
              sent.
            </li>
            <li>
              Anonymous usage events, crash reports, and ad-related data are
              sent to Google services (Firebase and AdMob), described below.
            </li>
          </ul>

          <h2 className={`text-2xl font-bold ${ACCENT}`}>
            Information We Process
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <strong>3D model and print files:</strong> STL, OBJ, 3MF
              (including slicer project files with plates and colours), sliced
              .gcode.3mf, G-code, and ZIP files you open from device storage,
              Google Drive, WhatsApp, the share sheet, or &quot;Open
              with&quot; are read and rendered entirely on your device. File
              contents are never transmitted.
            </li>
            <li>
              <strong>Measurements and estimates:</strong> dimensions, bed-fit
              checks, mesh health, overhang shading, weight, cost, and layer
              previews are calculated on your device and are not sent
              anywhere.
            </li>
            <li>
              <strong>Make templates:</strong> board cases, boxes, name tags,
              clips, and spacers are built on your device from the sizes you
              enter, including any custom board measurements. The finished file
              is written to the folder you pick when you tap Save, or handed to
              the app you pick when you tap Share. LayerPeek does not upload
              these files. Template settings leave your device only if you use
              COSMO AI, as described below.
            </li>
            <li>
              <strong>Recent files list:</strong> for files you recently
              opened, the app stores the file name, file type, file size, and
              a small thumbnail image locally so they appear on the home
              screen.
            </li>
            <li>
              <strong>Settings:</strong> your chosen printer (including any
              custom bed size), material, filament price, and other app
              preferences are stored locally on your device.
            </li>
            <li>
              <strong>Text you send to COSMO AI:</strong> when you send a
              message in the COSMO AI design chat, that message is sent off
              your device together with the context needed to answer it,
              described in the COSMO AI section below. Nothing is sent unless
              you send a message.
            </li>
            <li>
              <strong>A device identifier for COSMO AI:</strong> COSMO AI
              credits work without an account, so the service needs a way to
              recognise your device and keep your credit balance with you. For
              this, LayerPeek sends the Android <strong>App Set ID</strong>,
              with the Android ID as a supporting signal. The service stores
              them only as one-way hashes, never in a form that can be turned
              back into your device&apos;s identifiers, and they are never
              combined with your advertising ID. These identifiers are sent
              only when COSMO AI is available in the app.
            </li>
            <li>
              <strong>Usage analytics:</strong> Firebase Analytics receives
              anonymous usage events, for example &quot;file opened,
              type=3mf&quot; or &quot;bed fit check used&quot;, along with
              standard device information such as device model, OS version,
              app version, and approximate country. These events never include
              file names, file contents, or what you type to COSMO AI.
            </li>
            <li>
              <strong>Crash reports:</strong> Firebase Crashlytics receives
              crash and error reports (stack traces, device model, OS version,
              app version) so we can fix problems. Crash reports do not
              include your files or file names.
            </li>
            <li>
              <strong>Advertising data:</strong> Google AdMob may collect
              device identifiers (such as the advertising ID), device
              information, IP address, approximate location, and ad
              interaction data to deliver and measure ads.
            </li>
            <li>
              <strong>Purchase information:</strong> if you buy the optional
              Pro upgrade or a COSMO credit pack, Google Play Billing processes
              the payment. We receive only confirmation that the purchase is
              valid and what was bought; we never see your payment details.
            </li>
          </ul>

          <h2 className={`text-2xl font-bold ${ACCENT}`}>
            How We Use Information
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>To open, display, and measure 3D-printing files on your device.</li>
            <li>To build printable parts from Make templates on your device.</li>
            <li>
              To remember your recent files, printer, material, and price
              settings.
            </li>
            <li>
              To answer COSMO AI design chat messages and keep an accurate
              credit balance, if you use COSMO AI.
            </li>
            <li>
              To understand which features are used and where the app fails,
              so we can improve reliability and product quality.
            </li>
            <li>To show ads and to remove them for users who buy Pro.</li>
            <li>
              To adjust app settings remotely through Firebase Remote Config,
              without needing an app update.
            </li>
          </ul>

          <h2 className={`text-2xl font-bold ${ACCENT}`}>
            COSMO AI Design Chat and Credits
          </h2>
          <p>
            LayerPeek includes an optional design chat powered by{" "}
            <strong>COSMO</strong>, an AI service Cosmovex operates across its
            apps. You describe the part you want, and COSMO AI picks a Make
            template and fills in its settings. COSMO uses a credit balance,
            and that balance is shared by every Cosmovex app with COSMO AI that
            you use on the same device.{" "}
            <strong>You do not need an account to use it.</strong> Every Make
            template also works without COSMO AI.
          </p>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <strong>What is sent:</strong> the message you type, a limited
              number of earlier messages from the same chat, the template you
              are working on and its current settings (for a custom board, that
              includes the measurements you entered), and the list of templates
              and supported boards the app can build. Your 3D files, file names,
              and recent files list are not sent.
            </li>
            <li>
              <strong>Who processes it:</strong> COSMO AI runs on Google
              Firebase (Cloud Functions) in Cosmovex&apos;s COSMO project. It
              passes the request to Google&apos;s Gemini API, which generates
              the template settings and returns them. Google processes it under
              its own terms for that service.
            </li>
            <li>
              <strong>What stays on your phone:</strong> the chat itself is kept
              on your device and is cleared when you leave it.
            </li>
            <li>
              <strong>What we keep:</strong> the service keeps a credit ledger
              for your anonymous COSMO identity: your balance, how many credits
              each request cost, and the generated answer, so that retrying a
              request does not charge you twice.
            </li>
            <li>
              <strong>How your balance is recognised:</strong> using the hashed
              device identifiers described above. There is no name, email, or
              sign-in involved. This is what lets the same credits work in
              other Cosmovex apps on the same device.
            </li>
            <li>
              <strong>Credit packs:</strong> credits can be bought in the app
              through Google Play Billing. The purchase token is sent to the
              COSMO AI service so the credits can be added to your balance.
            </li>
            <li>
              <strong>Reporting an answer:</strong> each answer has a
              &quot;Report this answer&quot; button. It opens an email in your
              mail app, addressed to our support team, containing the answer
              you are reporting and the template it was for. Nothing is sent
              until you send that email. Separately, if an answer comes back
              unusable, the app tells the service which request it was so you
              are not charged for it.
            </li>
          </ul>

          <h2 className={`text-2xl font-bold ${ACCENT}`}>
            Deleting Your COSMO Account
          </h2>
          <p>
            You can ask us to delete your COSMO account from{" "}
            <strong>Settings &gt; COSMO Account &gt; Delete COSMO Account</strong>{" "}
            in LayerPeek. After you confirm, the app opens a prefilled email
            containing your anonymous COSMO account ID; nothing is deleted
            until you send it. For questions about deletion, email{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            .
          </p>
          <p>
            Deletion applies to your COSMO account in every Cosmovex app with
            COSMO AI, and it removes your credit balance, including any credits
            you paid for. Credits are not refundable. It does not affect your
            LayerPeek Pro purchase, which stays with your Google account. When
            we process the request, we permanently delete your credit balance,
            your generated AI answers, your usage history, and the hashed device
            identifiers associated with the account.
          </p>
          <p>
            <strong>One record is deliberately kept.</strong> When an account is
            deleted we retain a single anonymous entry recording that the
            one-time free credit grant has already been claimed on that device.
            We keep it for fraud prevention: without it, deleting and recreating
            an account would be an unlimited source of free credits. It contains
            only a one-way hash and a flag. It holds no name, email, file
            content, or anything that identifies you, and it is never used for
            any other purpose.
          </p>

          <h2 className={`text-2xl font-bold ${ACCENT}`}>
            Sharing a Summary Image
          </h2>
          <p>
            LayerPeek can create a summary image of a model (for example, a
            preview with its dimensions and estimates). The image is created on
            your device and is shared only when you tap Share, through the
            Android share sheet, to the app or person you choose. LayerPeek
            does not send it anywhere on its own.
          </p>

          <h2 className={`text-2xl font-bold ${ACCENT}`}>Local Storage</h2>
          <p>
            Your recent files list, thumbnails, and settings are stored in the
            app&apos;s private storage on your device and are not uploaded to
            any server. LayerPeek does not keep copies of the files you open
            beyond what is needed to display them. Local data remains on your
            device until you clear it, clear the app&apos;s storage, or
            uninstall the app.
          </p>

          <h2 className={`text-2xl font-bold ${ACCENT}`}>Network Access</h2>
          <p>
            Opening, viewing, and measuring files, and building parts with
            Make, does not require network access. The app uses the network
            only for ads, consent, Firebase Analytics, Firebase Crashlytics,
            Firebase Remote Config, Google Play purchases, and COSMO AI. No file
            contents or file names are ever transmitted. If you open a file
            stored in Google Drive or another cloud app, that app may download
            the file to your device before LayerPeek reads it; that transfer is
            handled by that app under its own privacy policy.
          </p>

          <h2 className={`text-2xl font-bold ${ACCENT}`}>
            Advertising and Consent
          </h2>
          <p>
            LayerPeek is free and shows ads through{" "}
            <strong>Google AdMob</strong>. Where required by law (for example
            in the European Economic Area, the United Kingdom, and
            Switzerland), the app uses Google&apos;s User Messaging Platform to
            ask for your consent before personalised ads are shown. You can
            change your choice at any time from{" "}
            <strong>Settings &gt; Ad Privacy Choices</strong> in the app. You
            can also reset or delete your advertising ID in your Android device
            settings.
          </p>

          <h2 className={`text-2xl font-bold ${ACCENT}`}>In-App Purchases</h2>
          <p>
            LayerPeek offers an optional one-time Pro purchase that removes
            ads, and optional COSMO AI credit packs. Purchases are processed
            through Google Play Billing and are subject to Google&apos;s terms
            and privacy policy.
          </p>

          <h2 className={`text-2xl font-bold ${ACCENT}`}>
            Third-Party Services
          </h2>
          <p>
            LayerPeek uses the following third-party services, each of which
            handles data under its own privacy policy:
          </p>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <strong>Google AdMob</strong> (ads) and Google User Messaging
              Platform (consent) -{" "}
              <a
                href="https://policies.google.com/privacy"
                className="text-[#0F6BBF] underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google Privacy Policy
              </a>{" "}
              and{" "}
              <a
                href="https://policies.google.com/technologies/ads"
                className="text-[#0F6BBF] underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                How Google uses information in ads
              </a>
            </li>
            <li>
              <strong>Google Play Billing</strong> (Pro and COSMO credit
              purchases) -{" "}
              <a
                href="https://policies.google.com/privacy"
                className="text-[#0F6BBF] underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google Privacy Policy
              </a>
            </li>
            <li>
              <strong>Firebase Analytics</strong>,{" "}
              <strong>Firebase Crashlytics</strong>, and{" "}
              <strong>Firebase Remote Config</strong> -{" "}
              <a
                href="https://firebase.google.com/support/privacy"
                className="text-[#0F6BBF] underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy and Security in Firebase
              </a>
            </li>
            <li>
              <strong>Firebase Authentication</strong>,{" "}
              <strong>Cloud Firestore</strong>,{" "}
              <strong>Cloud Functions</strong>, and{" "}
              <strong>App Check</strong> (COSMO AI) -{" "}
              <a
                href="https://firebase.google.com/support/privacy"
                className="text-[#0F6BBF] underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy and Security in Firebase
              </a>
            </li>
            <li>
              <strong>Google Gemini API</strong> (COSMO AI design chat) -{" "}
              <a
                href="https://policies.google.com/privacy"
                className="text-[#0F6BBF] underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google Privacy Policy
              </a>
            </li>
          </ul>

          <h2 className={`text-2xl font-bold ${ACCENT}`}>Data Sharing</h2>
          <p>
            We do not sell your files or personal data. Data is shared only
            with the service providers listed above, to the extent needed to
            show ads, process purchases, answer COSMO AI requests, measure
            usage, and diagnose crashes.
          </p>

          <h2 className={`text-2xl font-bold ${ACCENT}`}>
            Data Retention and Deletion
          </h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              Uninstalling LayerPeek, or clearing its storage in Android
              settings, deletes your recent files list, thumbnails, and
              settings from your device.
            </li>
            <li>
              Analytics and crash data are retained by Firebase according to
              Google&apos;s retention settings. Because LayerPeek has no
              account, this data is not linked to your name or email.
            </li>
            <li>
              COSMO AI data (your credit balance, credit history, and generated
              answers) stays with your anonymous COSMO identity until you
              delete your COSMO account, after which it is removed apart from
              the single anonymous free-grant record described above.
              Uninstalling LayerPeek alone does not delete it, because the same
              balance is used by other Cosmovex apps.
            </li>
            <li>
              To request deletion of analytics data associated with your
              installation, or for any other data question, email{" "}
              <a
                href="mailto:trionapps@gmail.com"
                className="text-[#0F6BBF] underline"
              >
                trionapps@gmail.com
              </a>
              .
            </li>
          </ul>

          <h2 className={`text-2xl font-bold ${ACCENT}`}>
            Children&apos;s Privacy
          </h2>
          <p>
            LayerPeek is not directed at children and is intended for users
            aged 13 and older. We do not knowingly collect personal information
            from children under 13. If you believe a child has provided
            information through the app, contact us and we will delete it.
          </p>

          <h2 className={`text-2xl font-bold ${ACCENT}`}>Security</h2>
          <p>
            We take reasonable steps to protect data processed by the app, and
            keeping your files on your device is part of that. No method of
            storage or transmission is completely secure.
          </p>

          <h2 className={`text-2xl font-bold ${ACCENT}`}>Your Choices</h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              You can change ad consent at any time in Settings &gt; Ad Privacy
              Choices.
            </li>
            <li>You can buy the one-time Pro upgrade to remove ads.</li>
            <li>
              You can use every Make template without COSMO AI. Nothing is sent
              to COSMO AI unless you send a message in the design chat.
            </li>
            <li>
              You can delete your COSMO account in Settings &gt; COSMO Account.
            </li>
            <li>
              You can reset your advertising ID or opt out of ad
              personalisation in your Android device settings.
            </li>
            <li>
              You can uninstall the app to remove all locally stored data.
            </li>
          </ul>

          <h2 className={`text-2xl font-bold ${ACCENT}`}>
            Changes to This Policy
          </h2>
          <p>
            We may update this Privacy Policy from time to time. When we do, we
            will post the updated version on this page and revise the effective
            date.
          </p>

          <h2 className={`text-2xl font-bold ${ACCENT}`}>Contact Us</h2>
          <p>
            For privacy-related questions about LayerPeek, contact{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            .
          </p>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-sm text-[#8A93A2]">
          &copy; 2026 Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
